import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../../components/FeatherIcon'
import { LoadingBlock } from '../../components/Spinner'
import { useIsNarrow } from '../../hooks/useIsNarrow'
import { useLibraryDeps } from '../../library/LibraryDeps'
import { Sentinel } from '../../library/Sentinel'
import { useToast } from '../../library/useToast'
import { MediaViewer } from '../../library/vault/MediaViewer'
import { createOfApi } from '../ofApi'
import { useCursorList, type CursorPage } from '../useCursorList'
import {
  batches,
  CHANNEL,
  goToDateMarker,
  isShown,
  nextMarker,
  pageUserIds,
  parseView,
  postsQuery,
  REFERER,
  SORT_DIRS,
  SORT_FIELDS,
  toDiscoverPost,
  toUserMap,
  viewerItems,
  viewQuery,
  type DiscoverPost,
  type DiscoverUser,
  type DiscoverView,
  type SortDir,
  type SortField,
} from './discover/discoverData'
import { BookmarkModal, CollaboratorsModal } from './discover/DiscoverModals'
import { DiscoverSearch } from './discover/DiscoverSearch'
import { DiscoverTile } from './discover/DiscoverTile'
import type { MorePageProps } from './morePage'
import '../../library/library.css'
import '../ofPages.css'
import './discover/discover.css'

type Raw = Record<string, unknown>
type PostsPayload = { list?: Raw[]; hasMore?: boolean; headMarker?: string; tailMarker?: string }

const postId = (p: DiscoverPost) => p.id
const FIELD_LABEL: Record<SortField, string> = { publish_date: 'latest', favorites_count: 'mostLiked', tips_summ: 'highestTips' }

/** Discover: the OFTV channel's videos (onlyfans.com/my/discover, captured 2026-10-22). */
export function DiscoverPage({ accountId, canWrite, query, onOpenPage, mobileNav }: MorePageProps) {
  const { t, i18n } = useTranslation('ofMore', { keyPrefix: 'discoverPage' })
  const { gateway } = useLibraryDeps()
  const of = useMemo(() => createOfApi(gateway), [gateway])
  const mobile = useIsNarrow()
  const { say, node: toast } = useToast()

  // ── The view: search and order live in the route, like OnlyFans' ?q=…&order=… ──
  const view = useMemo(() => parseView(query), [query])
  const setView = (patch: Partial<DiscoverView>) => onOpenPage('discover', viewQuery({ ...view, ...patch }), { replace: true })
  const [goToDate, setGoToDate] = useState<string | null>(null)
  const [searchOpen, setSearchOpen] = useState(() => Boolean(view.q))

  // ── The channel (users/oftv) ─────────────────────────────────────────────
  const [channel, setChannel] = useState<{ id: number; since: string } | null>(null)
  const [channelFailed, setChannelFailed] = useState(false)
  const [channelToken, setChannelToken] = useState(0)
  useEffect(() => {
    let live = true
    setChannel(null)
    setChannelFailed(false)
    of.get<Raw>(`users/${CHANNEL}`, {}, REFERER)
      .then((u) => {
        if (!live) return
        const id = Number(u?.id)
        if (!id) throw new Error('no channel')
        setChannel({ id, since: String(u?.firstPublishedPostDate ?? '').slice(0, 10) })
      })
      .catch(() => live && setChannelFailed(true))
    return () => {
      live = false
    }
    // accountId: another Account sees Discover through its own session.
  }, [of, accountId, channelToken])

  // ── Creators named on the tiles (users/list) ───────────────────────────
  const [users, setUsers] = useState<Record<number, DiscoverUser>>({})
  const asked = useRef(new Set<number>())
  useEffect(() => {
    asked.current = new Set()
    setUsers({})
  }, [accountId])
  const fetchUsers = useCallback(
    async (ids: number[], view: 'r' | 'tu') => {
      const todo = ids.filter((id) => id > 0 && !asked.current.has(id))
      if (!todo.length) return
      todo.forEach((id) => asked.current.add(id))
      await Promise.all(
        batches(todo).map(async (chunk) => {
          try {
            const found = toUserMap(await of.get(`users/list`, { [`${view}[]`]: chunk }, REFERER))
            setUsers((all) => ({ ...all, ...found }))
          } catch {
            chunk.forEach((id) => asked.current.delete(id))
          }
        }),
      )
    },
    [of],
  )

  // ── Posts ────────────────────────────────────────────────────────────────
  const fetchPosts = useCallback(
    async (cursor: string | null): Promise<CursorPage<DiscoverPost>> => {
      if (!channel) return { list: [], hasMore: false, next: null }
      const marker = cursor ?? (goToDate ? goToDateMarker(goToDate, view.dir) : null)
      const p = await of.get<PostsPayload>(`users/${channel.id}/posts`, postsQuery(view, marker, cursor === null), REFERER)
      const raw = p?.list ?? []
      // OnlyFans shows a page once its creators are known.
      await fetchUsers(pageUserIds(raw), 'r')
      const list = raw.map(toDiscoverPost)
      return { list, hasMore: p?.hasMore === true, next: nextMarker(view.dir, marker, p ?? {}, list) }
    },
    [of, channel, view, goToDate, fetchUsers],
  )
  const posts = useCursorList(fetchPosts, postId)
  const shown = useMemo(() => posts.items.filter(isShown), [posts.items])

  // A tile asks for any of its creators still unknown (users/list?tu[]=…).
  useEffect(() => {
    for (const p of shown) {
      const missing = p.userIds.filter((id) => !asked.current.has(id))
      if (missing.length) void fetchUsers(missing, 'tu')
    }
  }, [shown, fetchUsers])

  const setPost = (id: number, patch: Partial<DiscoverPost>) => posts.setItems((all) => all.map((p) => (p.id === id ? { ...p, ...patch } : p)))
  const bookmark = async (p: DiscoverPost) => {
    try {
      const r = await of.send<{ success?: boolean }>('POST', `posts/${p.id}/bookmarks`, REFERER)
      if (r?.success) setPost(p.id, { isAddedToBookmarks: true })
      else say(t('error'))
    } catch (e) {
      say(e instanceof Error && e.message ? e.message : t('error'))
    }
  }
  const unbookmark = async (p: DiscoverPost) => {
    try {
      const r = await of.send<{ success?: boolean }>('DELETE', `posts/${p.id}/bookmarks`, REFERER)
      if (r?.success) setPost(p.id, { isAddedToBookmarks: false })
      else say(t('error'))
    } catch (e) {
      say(e instanceof Error && e.message ? e.message : t('error'))
    }
  }

  // ── Viewer / modals ──────────────────────────────────────────────────────
  const viewer = useMemo(() => viewerItems(shown), [shown])
  const [viewing, setViewing] = useState<number | null>(null)
  const [collab, setCollab] = useState<number[] | null>(null)
  const [collectionFor, setCollectionFor] = useState<number | null>(null)
  const [menu, setMenu] = useState(false)

  const loadingFirst = !channelFailed && (!channel || (posts.loading && posts.items.length === 0))
  const failedFirst = channelFailed || (posts.failed && posts.items.length === 0)
  const disabled = loadingFirst || failedFirst
  const retry = () => (channelFailed ? setChannelToken((n) => n + 1) : void posts.loadMore())

  const dateLabel = goToDate
    ? new Date(`${goToDate}T12:00:00`).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric', year: 'numeric' })
    : t('goToDate')
  const pick = (patch: Partial<DiscoverView>) => {
    setMenu(false)
    setView(patch)
  }
  const sortMenu = menu && (
    <>
      <div className="lib-backdrop" onClick={() => setMenu(false)} />
      <div className="lib-pop lib-menu lib-menu--right ofdc-sort" role="menu" aria-label={t('sort')}>
        <Radio on={!goToDate} label={t('allTime')} onPress={() => (setMenu(false), setGoToDate(null))} />
        <label className={`lib-menu-item ofdc-date-item ${goToDate ? 'is-on' : ''}`}>
          <span className={`ofp-radio ${goToDate ? 'is-on' : ''}`} aria-hidden="true" />
          <span className="lib-menu-label">{dateLabel}</span>
          <input
            type="date"
            className="ofdc-date"
            aria-label={t('goToDate')}
            min={channel?.since || undefined}
            max={new Date().toISOString().slice(0, 10)}
            value={goToDate ?? ''}
            onChange={(e) => {
              setMenu(false)
              setGoToDate(e.target.value || null)
            }}
          />
        </label>
        <div className="ofp-menu-sep" />
        {SORT_FIELDS.map((f) => (
          <Radio key={f} on={view.field === f} label={t(FIELD_LABEL[f])} onPress={() => pick({ field: f })} />
        ))}
        <div className="ofp-menu-sep" />
        {SORT_DIRS.map((d: SortDir) => (
          <Radio key={d} on={view.dir === d} label={t(d)} onPress={() => pick({ dir: d })} />
        ))}
      </div>
    </>
  )

  const head = searchOpen ? (
    <header className="lib-main-head ofdc-head">
      <DiscoverSearch
        value={view.q}
        onSearch={(q) => setView({ q })}
        onClose={() => {
          setSearchOpen(false)
          if (view.q) setView({ q: '' })
        }}
      />
    </header>
  ) : (
    <header className="lib-main-head ofdc-head">
      <button type="button" className="lib-icon-btn" aria-label={t('back')} title={t('back')} onClick={() => onOpenPage('more')}>
        <FeatherIcon name="arrow-left" size={20} />
      </button>
      <h1 className="lib-main-title ofdc-title">{t('title')}</h1>
      <span className="lib-spacer" />
      <button type="button" className="lib-icon-btn" aria-label={t('search')} title={t('search')} disabled={disabled} onClick={() => setSearchOpen(true)}>
        <FeatherIcon name="search" size={19} />
      </button>
      <span className="ofdc-anchor">
        <button type="button" className={`lib-icon-btn ${menu ? 'is-on' : ''}`} aria-label={t('sort')} title={t('sort')} aria-haspopup="menu" disabled={disabled} onClick={() => setMenu((m) => !m)}>
          <FeatherIcon name="bar-chart-2" size={19} className="ofdc-rot" />
        </button>
        {sortMenu}
      </span>
    </header>
  )

  let body: ReactNode
  if (failedFirst) {
    body = (
      <div className="ofdc-state">
        <p>{t('error')}</p>
        <button type="button" className="lib-btn" onClick={retry}>
          {t('retry')}
        </button>
      </div>
    )
  } else if (loadingFirst) {
    body = <LoadingBlock pad="64px 0" label={t('loading')} />
  } else if (!shown.length && !posts.loading && !posts.hasMore) {
    body = (searchOpen || goToDate) && (
      <div className="ofdc-state">
        <FeatherIcon name="search" size={40} />
        <p>{t('nothingFound')}</p>
      </div>
    )
  } else {
    body = (
      <>
        <div className="ofdc-grid">
          {shown.map((p) => (
            <DiscoverTile
              key={p.id}
              post={p}
              users={users}
              canWrite={canWrite}
              onOpen={() => {
                const i = viewer.indexOf.get(p.id)
                if (i !== undefined) setViewing(i)
              }}
              onCollaborators={setCollab}
              onBookmark={() => bookmark(p)}
              onRemoveBookmark={() => unbookmark(p)}
              onAddToCollection={() => setCollectionFor(p.id)}
            />
          ))}
        </div>
        {posts.loading && <LoadingBlock pad="16px 0" label={t('loading')} />}
        {posts.failed && (
          <div className="ofdc-state ofdc-state--inline">
            <p>{t('error')}</p>
            <button type="button" className="lib-btn" onClick={() => void posts.loadMore()}>
              {t('retry')}
            </button>
          </div>
        )}
        {posts.hasMore && !posts.loading && !posts.failed && <Sentinel onVisible={() => void posts.loadMore()} token={posts.items.length} />}
      </>
    )
  }

  return (
    <div className={`lib ofp ofdc ${mobile ? 'lib--m ofdc--m' : ''}`}>
      <main className="lib-main">
        {head}
        <div className="ofdc-scroll">{body}</div>
      </main>
      {viewing !== null && (
        <MediaViewer
          items={viewer.items}
          index={viewing}
          onIndex={setViewing}
          onClose={() => setViewing(null)}
          hasMore={posts.hasMore}
          onLoadMore={() => posts.loadMore()}
          mobile={mobile}
        />
      )}
      {collab && <CollaboratorsModal ids={collab} users={users} onClose={() => setCollab(null)} />}
      {collectionFor !== null && (
        <BookmarkModal
          of={of}
          postId={collectionFor}
          canWrite={canWrite}
          onBookmarked={() => setPost(collectionFor, { isAddedToBookmarks: true })}
          onClose={() => setCollectionFor(null)}
          say={say}
        />
      )}
      {toast}
      {mobile && mobileNav}
    </div>
  )
}

function Radio({ on, label, onPress }: { on: boolean; label: string; onPress: () => void }) {
  return (
    <button type="button" role="menuitemradio" aria-checked={on} className="lib-menu-item" onClick={onPress}>
      <span className={`ofp-radio ${on ? 'is-on' : ''}`} aria-hidden="true" />
      <span className="lib-menu-label">{label}</span>
    </button>
  )
}
